import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild
} from "@angular/core";
import { ThemeAssetUploadResponse } from "../../admin/themes/models/theme.model";
import { ThemesApiService } from "../../admin/themes/services/themes.api.service";

/**
 * MIME types the backend (`ThemeAssetService`) accepts. The client mirrors this list so a file
 * the server would reject with a 400 never gets uploaded — keep the two in sync. Still narrower
 * than the design copy, which also lists MP4; the backend does not support video yet.
 */
export const ALLOWED_IMAGE_TYPES: readonly string[] = [
    "image/png",
    "image/jpeg",
    "image/webp",
    "image/gif",
    "image/svg+xml"
];

/** Maximum asset size the backend accepts (5 MB). */
export const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * Reusable image picker: file-picker + drag/drop, type/size validation, preview, and replace.
 * Uploads the chosen file via `ThemesApiService.uploadThemeAsset` and emits the resulting
 * `{ key, url }` (key matches `^public/.+`). Does no cropping — visual fit is a display-time
 * concern (`object-fit`/`background-size: cover`) at the render site.
 */
@Component({
    selector: "app-image-upload",
    templateUrl: "./image-upload.component.html",
    styleUrl: "./image-upload.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true
})
export class ImageUploadComponent {
    private readonly themesApi = inject(ThemesApiService);

    /** Existing image URL to preview in edit mode (e.g. a theme's current logo). */
    readonly initialUrl = input<string | null>(null);
    /** Accessible name for the control, woven into aria labels (e.g. "tenant logo"). */
    readonly label = input("image");
    /** Prompt shown in the empty tile (e.g. "Upload Image", "Drag and drop badge here"). */
    readonly promptText = input("Upload Image");
    /**
     * Keeps the empty-state backdrop visible behind an uploaded image. Since the preview is
     * `object-fit: contain`, an image that doesn't match the tile's aspect ratio leaves gaps —
     * showing the same backdrop as before upload makes that letterboxing obvious.
     */
    readonly showBackdrop = input(false);

    /** Emits the uploaded asset `{ key, url }` on a successful upload. */
    readonly uploaded = output<ThemeAssetUploadResponse>();

    private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>("fileInput");

    private readonly uploadedUrl = signal<string | null>(null);

    readonly uploading = signal(false);
    readonly errorMessage = signal<string | null>(null);

    /** Preview precedence: the just-uploaded URL, otherwise the provided initial URL. */
    readonly previewUrl = computed(() => this.uploadedUrl() ?? this.initialUrl());

    readonly acceptAttr = ALLOWED_IMAGE_TYPES.join(",");

    openPicker(): void {
        this.fileInput().nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.handleFile(file);
        }
        // Reset the input so re-selecting the same file still fires a `change` event.
        input.value = "";
    }

    onDragOver(event: DragEvent): void {
        // Required so the element is a valid drop target; the drop itself is handled in onDrop.
        event.preventDefault();
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        const file = event.dataTransfer?.files?.[0];
        if (file) {
            this.handleFile(file);
        }
    }

    private handleFile(file: File): void {
        // Ignore a new file while an upload is already in flight.
        if (this.uploading()) {
            return;
        }
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            this.errorMessage.set("Unsupported file type. Use JPEG, PNG, GIF, WebP, or SVG.");
            return;
        }
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            this.errorMessage.set("File is too large. Maximum size is 5 MB.");
            return;
        }

        this.errorMessage.set(null);
        this.uploading.set(true);

        this.themesApi.uploadThemeAsset(file).subscribe({
            next: response => {
                this.uploadedUrl.set(response.url);
                this.uploading.set(false);
                this.uploaded.emit(response);
            },
            error: () => {
                this.errorMessage.set("Upload failed. Please try again.");
                this.uploading.set(false);
            }
        });
    }
}
